// @flow

import React from "react";
import { localize } from "redux-i18n";
import type { ClosedReferralsFilters } from "@modules/reporting/reducer/referral-tracker/types";
import { Button, DatePicker, ComboBox, View, Text } from "@components";
import moment from "moment";
import { SUPPLIER_REFERRAL_STATUSES } from "@modules/reporting/constants/reports";
import type { OptionType } from "@components";

type OwnProps = $Exact<{
  onFilterChange: (filters: ClosedReferralsFilters) => void,
  onReset: () => void,
  onExport: (filters: ClosedReferralsFilters) => void,
  statuses: string[],
  suppliers: { value: number, label: string }[],
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
}>;

type OwnState = {
  fromDate: string,
  toDate: string,
  status: string,
  referralType: null,
  referralProcessId: number,
};

export class ClosedReferralsFilter extends React.Component<Props, OwnState> {
  state = {
    fromDate: "",
    toDate: "",
    status: SUPPLIER_REFERRAL_STATUSES.ALL,
    referralType: null,
    referralProcessId: null,
  };

  get outcomeStatuses() {
    return [SUPPLIER_REFERRAL_STATUSES.ALL, ...this.props.statuses].map(item => ({
      label: this.props.t(item),
      value: item,
    }));
  }

  getLastNDays = (daysCount: number) => () => {
    const fromDate = moment()
      .subtract(daysCount, "days")
      .format("YYYY-MM-DD");
    const toDate = moment().format("YYYY-MM-DD");

    this.setState({
      fromDate,
      toDate,
    });

    this.props.onFilterChange({
      ...this.state,
      fromDate,
      toDate,
    });
  };

  onDateChange = (fieldName: string) => (value: string) => {
    const filters = {
      ...this.state,
      [fieldName]: value.length ? moment(value).format("YYYY-MM-DD") : "",
    };

    this.setState({
      ...filters,
    });

    this.props.onFilterChange({
      ...filters,
    });
  };

  statusChange = (val: OptionType) => {
    const filters = {
      ...this.state,
      status: val,
    };

    this.setState({
      ...filters,
    });

    this.props.onFilterChange({
      ...filters,
    });
  };

  referralSupplierChange = (val: OptionType) => {
    const filters = {
      ...this.state,
      referralProcessId: val,
    };

    this.setState({
      ...filters,
    });

    this.props.onFilterChange({
      ...filters,
    });
  };

  referralTypeChange = (val: OptionType) => {
    const filters = {
      ...this.state,
      referralType: val,
    };

    this.setState({
      ...filters,
    });

    this.props.onFilterChange({
      ...filters,
    });
  };

  onReset = () => {
    this.setState({
      fromDate: "",
      toDate: "",
      status: SUPPLIER_REFERRAL_STATUSES.ALL,
      referralType: null,
      referralProcessId: null,
    });

    this.props.onReset();
  };

  onExport = () => {
    this.props.onExport({ ...this.state });
  };

  render() {
    const fromDate = this.state.fromDate.length ? moment(this.state.fromDate) : "";
    const toDate = this.state.toDate.length ? moment(this.state.toDate) : "";

    return (
      <View direction="row" flex="1" alignItems="center" justifyContent="space-between">
        <View direction="column" gap={4}>
          <View direction="row" gap="8" alignItems="center">
            <View direction="row" gap="2" alignItems="center">
              <Button color="pumpkin-orange" onClick={this.getLastNDays(30)}>
                {this.props.t("LAST_N_DAYS", {
                  count: 30,
                })}
              </Button>
              <Button color="pumpkin-orange" onClick={this.getLastNDays(90)}>
                {this.props.t("LAST_N_DAYS", {
                  count: 90,
                })}
              </Button>
            </View>

            <View gap="2" direction="row" alignItems="center">
              <Text>{this.props.t("FROM")}:</Text>
              <DatePicker value={fromDate} onChange={this.onDateChange("fromDate")} />
            </View>

            <View gap="2" direction="row" alignItems="center">
              <Text>{this.props.t("TO")}:</Text>
              <DatePicker value={toDate} onChange={this.onDateChange("toDate")} />
            </View>

            <View gap="2" direction="row" alignItems="center" flex="1">
              <Text>{this.props.t("OUTCOME")}:</Text>
              <View width="300px">
                <ComboBox
                  withoutEmptyOption={true}
                  value={this.outcomeStatuses.find(status => status.value === this.state.status)}
                  onChange={this.statusChange}
                  options={this.outcomeStatuses}
                />
              </View>
            </View>
          </View>

          <View direction="row" gap="8" justifyContent="flex-start" alignItems="center">
            <View gap="2" direction="row" alignItems="center">
              <Text>{this.props.t("SUPPLIER")}:</Text>
              <View width="300px">
                <ComboBox
                  withoutEmptyOption={true}
                  value={this.state.referralProcessId}
                  onChange={this.referralSupplierChange}
                  options={this.props.suppliers}
                />
              </View>
            </View>
            <View gap="2" direction="row" alignItems="center">
              <Text>{this.props.t("REFERRAL_TYPE")}:</Text>
              <View width="300px">
                <ComboBox
                  withoutEmptyOption={true}
                  value={this.state.referralType}
                  onChange={this.referralTypeChange}
                  options={[
                    {
                      label: this.props.t("ALL"),
                      value: null,
                    },
                    {
                      label: this.props.t("EMPLOYEE_REFERRAL"),
                      value: "EMPLOYEE_REFERRAL",
                    },
                    {
                      label: this.props.t("MANAGER_REFERRAL"),
                      value: "MANAGER_REFERRAL",
                    },
                    {
                      label: this.props.t("MANUAL_REFERRAL"),
                      value: "MANUAL_REFERRAL",
                    },
                  ]}
                />
              </View>
            </View>
          </View>
        </View>
        <View direction="row" gap="2" alignItems="center">
          <Button className={"primary "} onClick={this.onReset}>
            {this.props.t("RESET")}
          </Button>
          <Button materialIcon="file_download" className={"primary"} onClick={this.onExport}>
            {this.props.t("EXPORT")}
          </Button>
        </View>
      </View>
    );
  }
}

export default localize()(ClosedReferralsFilter);
